import React, { FC, memo, useState } from 'react';
import { TextField } from '@mui/material';
import { useIntl } from 'react-intl';
import FileUploader, { FileObj } from 'src/app/common/components/FileUploader';
import { createBlob, getBlob } from 'src/app/common/network';
import { fileUpload } from 'src/app/common/utils';
import { useDispatch } from 'react-redux';
import { AlertType, appendAlertItem } from '@pruforce/common-adminweb-sdk';
import { PromotionImageItemType } from './PromotionImageDetailForm';
import { ImageItem } from '../../../types/promotion-image-type';
import Form from 'src/app/common/components/Form';
import { debounce } from 'lodash';
import { useStyles } from '../../../styles/promotion-image-style';

type PromotionImageItemProps = {
  index: number;
  itemInput: PromotionImageItemType;
  onChange: (index: number, fieldName: string, value: string | ImageItem) => void;
};

const PromoteImageFormItem: FC<PromotionImageItemProps> = memo(({ index, itemInput, onChange }) => {
  const [item] = useState<PromotionImageItemType>(itemInput);

  const dispatch = useDispatch();
  const { classes } = useStyles();
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const DEFAULT_REQUIRED_RULES = [{ required: true, message: Translation('component.form-required-text') }];

  const debouncedHandleChange = debounce((indexNew: number, fieldName: string, value: any) => {
    onChange(indexNew, fieldName, value);
  }, 500);

  const handleChange = (fieldName: string, value: any) => {
    debouncedHandleChange(index, fieldName, value);
  };

  const handleUploadFile = async ({
    file,
    fileName,
    fileType,
    description = '',
  }: {
    file: any;
    fileName: string;
    fileType: string;
    description?: string;
  }) => {
    try {
      const createBlobRes = await createBlob(
        {
          mimeType: fileType,
          accessLevel: 'anonymous',
          module: 'bdm-promotion',
        },
        dispatch,
      );
      await fileUpload(createBlobRes.url, file, () => {});
      const blobDetail = await getBlob({ resourceIds: createBlobRes.blobId }, dispatch);

      const result = blobDetail[0];
      if (result) {
        dispatch(
          appendAlertItem([
            {
              severity: AlertType.SUCCESS,
              title: 'Success',
              content: `Upload successfully - ${fileName}`,
            },
          ]),
        );
        return {
          id: result.blobId,
          fileName: fileName,
          url: result.url,
          type: fileType,
        };
      }
    } catch (err) {
    } finally {
    }
  };

  return (
    <div>
      <div className={classes.sectionMargin}>
        <div className={classes.rowContainer}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>
              {'Promotion Image Name'}
              <span className={classes.mandatory}>*</span> :
            </span>
          </div>
          <div style={{ flexGrow: 1 }}>
            <Form.Item name={['name', itemInput.key]} rules={DEFAULT_REQUIRED_RULES} initialValue={item.name}>
              <TextField
                style={{ width: '100%' }}
                margin="dense"
                variant="outlined"
                className={classes.root}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder={Translation('app.input.placeholder.please-enter')}
                inputProps={{ maxLength: 30 }}
              />
            </Form.Item>
            {item.errorNameMessage && <div className={classes.errorText}>{item.errorNameMessage}</div>}
          </div>
        </div>
      </div>
      <div className={classes.sectionMargin}>
        <div className={classes.rowContainer} style={{ marginBottom: 5 }}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>
              {'Upload Image'}
              <span className={classes.mandatory}>*</span> :
            </span>
          </div>
          <div style={{ flexGrow: 1 }}>
            <Form.Item name={['image', itemInput.key]} rules={DEFAULT_REQUIRED_RULES} initialValue={item.image}>
              <FileUploader
                color="secondary"
                showAllowText={Translation('component.hint.resource.bdm-promotion-image')}
                upload={handleUploadFile}
                allowedFileTypes={['jpg', 'png', 'jpeg']}
                maxFileSize={20}
                showFileSize={true}
                showPreview
                onChange={(result) => handleChange('image', result)}
              />
            </Form.Item>
          </div>
        </div>
      </div>
    </div>
  );
});

export default PromoteImageFormItem;
